import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { LoadingSkeleton, ModalSheet } from "@a2z/ui";
import { ErrorState } from "@/shared/components/ErrorState";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import type { SellerTransactionDetail } from "@/lib/api/types";
import { useSellerTransactionQuery } from "@/modules/transactions/services/useSellerTransactionQuery";
import { TransactionStatusPill } from "@/modules/transactions/components/TransactionStatusPill";
import {
  canShowCheckoutUrl,
  formatTransactionDateTime,
  getLinkedOrderStatusLabel,
  getProviderLabel,
  getTransactionStatusLabel,
} from "@/modules/transactions/lib/transaction-display";

type TransactionDetailDrawerProps = {
  storeSlug: string;
  /** Null closes the drawer. */
  transactionId: string | null;
  locale: string;
  orderHref: (orderId: string) => string;
  onClose: () => void;
};

/** Label + value row. Values that are LTR identifiers pass `mono` so they read
 * correctly beside Arabic labels. */
function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 py-[7px] text-[12.5px]">
      <span className="shrink-0 text-[color:var(--text-secondary)]">
        {label}
      </span>
      <span className="min-w-0 text-end font-semibold text-[color:var(--text-primary)]">
        {children}
      </span>
    </div>
  );
}

/** Gateway identifiers are always Latin/numeric — wrap in `<bdi>` so they stay
 * intact and correctly ordered inside the RTL Arabic layout. */
function MonoValue({ value }: { value: string }): ReactElement {
  return (
    <span className="block truncate font-mono text-[11.5px]">
      <bdi>{value}</bdi>
    </span>
  );
}

function SectionTitle({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="mb-1.5 text-[12.5px] font-bold text-[color:var(--text-primary)]">
      {children}
    </div>
  );
}

function DetailBody({
  transaction,
  locale,
  orderHref,
}: {
  transaction: SellerTransactionDetail;
  locale: string;
  orderHref: (orderId: string) => string;
}): ReactElement {
  const t = useTranslations();
  const notAvailable = t(keys.transactions.detailNotAvailable);
  const amountLabel = formatCurrency(
    transaction.amount,
    locale,
    transaction.currency,
  );
  // Aliased into a local const so the type predicate narrows it to `string`
  // for the JSX below (TS does not narrow a property access this way).
  const { checkoutUrl } = transaction;
  const showCheckoutUrl = canShowCheckoutUrl(transaction.status, checkoutUrl);

  return (
    <div className="flex flex-col gap-4">
      {/* Amount + status headline */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[22px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            <bdi>{amountLabel}</bdi>
          </span>
          <TransactionStatusPill status={transaction.status} />
        </div>
      </section>

      {/* Gateway */}
      <section className="rounded-[12px] border border-[color:var(--line2)] p-3">
        <SectionTitle>{t(keys.transactions.detailGatewaySection)}</SectionTitle>
        <div className="divide-y divide-[color:var(--hair)]">
          <DetailRow label={t(keys.transactions.detailProvider)}>
            {getProviderLabel(t, transaction.provider)}
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailProviderTxnId)}>
            {transaction.providerTxnId ? (
              <MonoValue value={transaction.providerTxnId} />
            ) : (
              notAvailable
            )}
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailProviderRef)}>
            {transaction.providerRef ? (
              <MonoValue value={transaction.providerRef} />
            ) : (
              notAvailable
            )}
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailProviderStatusCode)}>
            {transaction.providerStatusCode ? (
              <MonoValue value={transaction.providerStatusCode} />
            ) : (
              notAvailable
            )}
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailProviderStatusText)}>
            {transaction.providerStatusText ?? notAvailable}
          </DetailRow>
        </div>
        {showCheckoutUrl ? (
          <a
            href={checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex h-9 items-center justify-center rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-3.5 text-[12.5px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--hover)]"
          >
            {t(keys.transactions.detailCheckoutUrlAction)}
          </a>
        ) : null}
      </section>

      {/* Timestamps */}
      <section className="rounded-[12px] border border-[color:var(--line2)] p-3">
        <SectionTitle>{t(keys.transactions.detailTimingSection)}</SectionTitle>
        <div className="divide-y divide-[color:var(--hair)]">
          <DetailRow label={t(keys.transactions.detailCreatedAt)}>
            <bdi>
              {formatTransactionDateTime(transaction.createdAt, locale)}
            </bdi>
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailUpdatedAt)}>
            <bdi>
              {formatTransactionDateTime(transaction.updatedAt, locale)}
            </bdi>
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailLastEventAt)}>
            {transaction.lastEventAt ? (
              <bdi>
                {formatTransactionDateTime(transaction.lastEventAt, locale)}
              </bdi>
            ) : (
              notAvailable
            )}
          </DetailRow>
        </div>
      </section>

      {/* Linked order */}
      <section className="rounded-[12px] border border-[color:var(--line2)] p-3">
        <SectionTitle>{t(keys.transactions.detailOrderSection)}</SectionTitle>
        <div className="divide-y divide-[color:var(--hair)]">
          <DetailRow label={t(keys.transactions.colOrder)}>
            <Link
              to={orderHref(transaction.order.id)}
              className="underline-offset-2 hover:text-[color:var(--accent-600)] hover:underline"
            >
              <bdi>#{transaction.order.orderCode}</bdi>
            </Link>
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailOrderStatus)}>
            {getLinkedOrderStatusLabel(t, transaction.order.status)}
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailOrderTotal)}>
            <bdi>
              {formatCurrency(
                transaction.order.totalAmount,
                locale,
                transaction.currency,
              )}
            </bdi>
          </DetailRow>
          <DetailRow label={t(keys.transactions.detailOrderPaid)}>
            <bdi>
              {formatCurrency(
                transaction.order.paidAmount,
                locale,
                transaction.currency,
              )}
            </bdi>
          </DetailRow>
        </div>
      </section>

      {/* Event timeline — newest first, as the API returns it. */}
      <section className="rounded-[12px] border border-[color:var(--line2)] p-3">
        <SectionTitle>{t(keys.transactions.detailTimelineTitle)}</SectionTitle>
        {transaction.events.length === 0 ? (
          <p className="py-2 text-[12.5px] text-[color:var(--text-muted)]">
            {t(keys.transactions.detailTimelineEmpty)}
          </p>
        ) : (
          <ol className="flex flex-col">
            {transaction.events.map((event, index) => {
              const isLast = index === transaction.events.length - 1;
              return (
                <li key={event.id} className="flex gap-2.5">
                  {/* Rail: dot + connector. Sits at the inline-start edge, so it
                      flips with direction automatically. */}
                  <div
                    aria-hidden="true"
                    className="flex flex-col items-center pt-[7px]"
                  >
                    <span
                      className="h-[7px] w-[7px] shrink-0 rounded-full"
                      style={{
                        background:
                          index === 0
                            ? "var(--accent-500)"
                            : "var(--border-strong)",
                      }}
                    />
                    {!isLast ? (
                      <span className="mt-1 w-px flex-1 bg-[color:var(--hair)]" />
                    ) : null}
                  </div>
                  <div
                    className={`min-w-0 flex-1 ${isLast ? "pb-1" : "pb-3.5"}`}
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[12.5px] font-semibold text-[color:var(--text-primary)]">
                        {event.mappedStatus
                          ? getTransactionStatusLabel(t, event.mappedStatus)
                          : t(keys.transactions.detailEventUnmapped)}
                      </span>
                      {event.providerStatusCode ? (
                        <span className="rounded-full bg-[color:var(--soft2)] px-1.5 py-px font-mono text-[10.5px] text-[color:var(--text-muted)]">
                          <bdi>{event.providerStatusCode}</bdi>
                        </span>
                      ) : null}
                    </div>
                    {event.providerStatusText ? (
                      <p className="mt-0.5 text-[11.5px] text-[color:var(--text-secondary)]">
                        {event.providerStatusText}
                      </p>
                    ) : null}
                    <p className="mt-0.5 text-[11px] text-[color:var(--text-muted)]">
                      <bdi>
                        {formatTransactionDateTime(
                          event.occurredAt ?? event.receivedAt,
                          locale,
                        )}
                      </bdi>
                      {event.occurredAt ? null : (
                        <>
                          {" · "}
                          {t(keys.transactions.detailEventReceivedAt)}
                        </>
                      )}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

/**
 * Transaction detail as a side drawer (bottom sheet on mobile), matching how the
 * orders module shows an order (`OrderDetailModal` → `ModalSheet variant="drawer"`).
 * Open state is owned by the page and driven by the `?transaction=<id>` search
 * param, so browser back closes it.
 */
export function TransactionDetailDrawer({
  storeSlug,
  transactionId,
  locale,
  orderHref,
  onClose,
}: TransactionDetailDrawerProps): ReactElement {
  const t = useTranslations();
  const isOpen = transactionId !== null;
  const {
    data: transaction,
    isLoading,
    error,
    refetch,
  } = useSellerTransactionQuery({ storeSlug, transactionId });

  return (
    <ModalSheet
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      variant="drawer"
      surface="content"
    >
      <header className="flex shrink-0 items-start justify-between gap-3 px-[18px] pb-3 pt-4">
        <div className="min-w-0">
          <h2 className="truncate text-[16px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.transactions.detailTitle)}
          </h2>
          {transaction ? (
            <div className="mt-0.5 truncate text-[12.5px] text-[color:var(--text-muted)]">
              <bdi>#{transaction.orderCode}</bdi>
              {" · "}
              {getProviderLabel(t, transaction.provider)}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t(keys.common.close)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--card)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)] lg:h-8 lg:w-8"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col px-3.5 pb-3.5">
        <div className="min-h-0 flex-1 overflow-y-auto rounded-[16px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-4">
          {error && !isLoading ? (
            <ErrorState
              message={t(keys.transactions.detailLoadError)}
              onRetry={refetch}
            />
          ) : isLoading || !transaction ? (
            <LoadingSkeleton variant="card" rows={6} />
          ) : (
            <DetailBody
              transaction={transaction}
              locale={locale}
              orderHref={orderHref}
            />
          )}
        </div>
      </div>
    </ModalSheet>
  );
}
